/**
 * The overtime span, in ONE place.
 *
 * There were two implementations of this arithmetic and they disagreed at exactly
 * the case that matters (audit, 2026-08-25):
 *
 *   start  end    | create dialog (ApprovalList) | list column (OvertimeApprovals)
 *   18:00  21:00  | 3                            | 3
 *   22:00  02:00  | (blank)                      | 4
 *   23:30  00:30  | (blank)                      | 1
 *   09:00  09:00  | (blank)                      | 0
 *
 * The create dialog required `minutes > 0` and produced nothing otherwise; the list
 * added 24 h. So a night shift showed no total while being typed and four hours once
 * saved. The comment above the list's copy asserted the opposite — "one rule, so a
 * stored total and a derived one cannot disagree" — which is what a second
 * implementation always ends up claiming (svdy-rules §10.5).
 *
 * The rule itself is NOT new. `app/attendance/calculation.py` decided it for shifts:
 *
 *   "The explicit flag, or the times themselves saying so — a 22:00→06:00 shift
 *    crosses whether or not anybody ticked the box, and relying on the flag alone
 *    made the behaviour depend on how the row was created."
 *
 * So `end <= start` means the end is on the NEXT day, and `_timetable_expected_minutes`
 * adds 24 h in exactly that case. Overtime now cites the same rule instead of having
 * its own accidental one.
 */

/** Minutes since midnight, or null when the value is not `HH:MM`. */
function minutesOfDay(value?: string | null): number | null {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/**
 * True when the end time falls on the day AFTER the start time.
 *
 * `end === start` counts as crossing: a shift that ends at the same clock time it
 * began is a 24-hour one, not a zero-hour one. That is the same reading
 * `_timetable_expected_minutes` takes (`if tt.cross_midnight or end <= start`), and
 * the reason the UI must SAY so — a 24 h overtime entry is either a data-entry slip
 * or something an approver needs to see, and both are better than a silent 0.
 */
export function endsNextDay(start?: string | null, end?: string | null): boolean {
  const from = minutesOfDay(start);
  const to = minutesOfDay(end);
  if (from === null || to === null) return false;
  return to <= from;
}

/**
 * Hours between two `HH:MM` times, to two decimals, or null when either is missing
 * or malformed. Wraps past midnight per `endsNextDay`.
 */
export function overtimeHours(start?: string | null, end?: string | null): number | null {
  const from = minutesOfDay(start);
  const to = minutesOfDay(end);
  if (from === null || to === null) return null;
  const minutes = to > from ? to - from : to + 24 * 60 - from;
  return Math.round((minutes / 60) * 100) / 100;
}
